import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Table, Space, Tooltip, Button, PageHeader, Tag, Popconfirm } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { handleApiSuccess, handleApiError, MyBreadcrumb, confirmDelete } from "utils";
import {
  billingRecurringListService,
  billingRecurringDeleteService,
  billingRecurringGenerateService,
} from "services/billing";
import { FREQUENCIES } from "../constants";

export default function RecurringIndex() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  const freqLabel = (v) => (FREQUENCIES.find((f) => f.value === v) || {}).label || v;
  const statusColor = { ACTIVE: "green", PAUSED: "orange", ENDED: "default" };

  const columns = [
    {
      title: "Title",
      key: "title",
      render: (t, r) => r.title || (r.company && r.company.company_name) || "Template",
    },
    {
      title: "Customer",
      key: "customer",
      render: (t, r) => (r.company && r.company.company_name) || "-",
    },
    { title: "Frequency", dataIndex: "frequency", key: "frequency", render: freqLabel },
    { title: "Next Run", dataIndex: "next_run_date", key: "next_run_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date", render: (t) => t || "—" },
    {
      title: "Generated",
      dataIndex: "generated_count",
      key: "generated_count",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (t) => <Tag color={statusColor[t] || "default"}>{t}</Tag>,
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="small">
          <Tooltip title="Generate invoice now">
            <Popconfirm
              title="Generate an invoice from this template now?"
              onConfirm={() => generateNow(record._id)}
            >
              <Button shape="circle" icon={<ThunderboltOutlined />} />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              onClick={() => history.push("/billing/recurring/update/" + record._id)}
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              onClick={() => deleteRecord(record._id)}
              shape="circle"
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setIsLoading(true);
    billingRecurringListService()
      .then((response) => {
        setIsLoading(false);
        setDataSource(response.data.items || []);
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const generateNow = (id) => {
    billingRecurringGenerateService(id)
      .then((response) => {
        handleApiSuccess(response);
        loadData();
      })
      .catch((error) => handleApiError(error));
  };

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      billingRecurringDeleteService(id)
        .then((response) => {
          handleApiSuccess(response);
          loadData();
        })
        .catch((error) => {
          setIsLoading(false);
          handleApiError(error);
        });
    });
  };

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Billing", path: "" },
          { label: "Recurring", path: "" },
        ]}
      />
      <PageHeader
        ghost={false}
        avatar={{ icon: <SyncOutlined /> }}
        title="Recurring Invoice Templates"
        subTitle="Auto-generate invoices on a schedule with service-month tracking"
        extra={[
          <Button
            key="create"
            shape="round"
            onClick={() => history.push("/billing/recurring/create")}
            type="primary"
            icon={<PlusCircleOutlined />}
          >
            New Template
          </Button>,
        ]}
      />
      <Table dataSource={dataSource} columns={columns} loading={isLoading} rowKey="_id" />
    </div>
  );
}
